/**
 * Copyright (C) Siemens AG 1996-2024 ALL RIGHTS RESERVED.
 */

import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  inject,
} from '@angular/core';
import {FormControl, FormGroup, Validators} from '@angular/forms';
import {User} from '../../../models/User';
import {ModalService} from '@siemens/ix-angular';
import {Router} from '@angular/router';
import {Role} from '../../../models/user-management/Role';
import {Organization} from '../../../models/Organization';
import {UserService} from '../../../services/user.service';
import {Language, LanguageService} from '../../../services/language.service';

const TEXT_MIN_LEN = 2;
const TEXT_MAX_LEN = 100;

const USER_FORM_TYPE_ADMIN_SAVE = 'admin-save';
const USER_FORM_TYPY_INVITE = 'invite';
const USER_FORM_TYPE_SAVE = 'save';

@Component({
  selector: 'app-user-form',
  templateUrl: './user-form.component.html',
  styleUrl: './user-form.component.scss',
  standalone: false,
})
export class UserFormComponent implements OnInit, OnChanges {
  private readonly userService = inject(UserService);
  private readonly languageService = inject(LanguageService);
  private readonly modalService = inject(ModalService);
  private readonly router = inject(Router);

  @ViewChild('cancelModal', {read: TemplateRef})
  cancelModalRef!: TemplateRef<HTMLElement>;

  @Input() userFormType: 'invite' | 'save' | 'admin-save' = 'invite';
  @Input() cancelRoute = '';
  @Input() cancelEvent: EventEmitter<void> = new EventEmitter<void>();
  @Input() loading = false;

  @Input() userData: User = {
    id: '',
    firstName: '',
    lastName: '',
    email: '',
    organization: {
      id: '',
      name: '',
    },
    language: '',
    region: '',
    status: '',
    roles: [],
  };
  @Output() userDataChange: EventEmitter<User> = new EventEmitter<User>();

  availableRoles: Role[] = [];
  manageableRoles: Role[] = [];
  availableOrganizations?: Organization[];
  availableLanguages: Language[] = [];
  userForm!: FormGroup;

  async ngOnChanges(changes: SimpleChanges) {
    if (
      changes['userData']?.currentValue?.email &&
      this.availableOrganizations
    ) {
      this.createUserForm();
    }
  }

  async ngOnInit() {
    // Load available languages from manifest
    this.languageService.getAvailableLanguages().subscribe((languages) => {
      this.availableLanguages = languages;
    });

    this.userService.userDataChange.subscribe(async (user) => {
      if (user) {
        this.availableOrganizations = [user.organization];
        // If you invite a user, the organization is set to the organization of the user who invited
        if (this.userFormType === USER_FORM_TYPY_INVITE) {
          this.userData.organization = user.organization;
        }
      }
    });
    if (this.userFormType === 'admin-save' || this.userFormType === 'invite') {
      if (
        !this.userService.manageableRoles ||
        this.userService.manageableRoles.length === 0
      ) {
        this.manageableRoles = await this.userService.loadAvailableRoles(
          this.userData.organization.id,
          true,
        );
      } else {
        this.manageableRoles = this.userService.manageableRoles;
      }
    }

    this.userService.availableRolesChange.subscribe((roles) => {
      if (roles && roles.length > 0) {
        this.availableRoles = roles;
      } else {
        if (this.userData.organization.roles) {
          this.availableRoles = this.userData.organization.roles?.map(
            (role) => {
              return {
                id: role.id,
                label: role.label,
              };
            },
          );
        }
      }
      this.createUserForm();
    });
    this.cancelEvent.subscribe(() => {
      this.onCancel();
    });
  }

  createUserForm() {
    if (this.userData) {
      if (this.availableOrganizations?.length === 0) {
        this.availableOrganizations = [this.userData.organization];
      }
      this.userForm = new FormGroup({
        firstName: new FormControl(
          {
            value: this.userData.firstName,
            disabled: this.userFormType !== USER_FORM_TYPY_INVITE,
          },
          [
            Validators.required,
            Validators.minLength(TEXT_MIN_LEN),
            Validators.pattern('.*\\S.*'),
            Validators.maxLength(TEXT_MAX_LEN),
          ],
        ),
        lastName: new FormControl(
          {
            value: this.userData.lastName,
            disabled: this.userFormType !== USER_FORM_TYPY_INVITE,
          },
          [
            Validators.required,
            Validators.minLength(TEXT_MIN_LEN),
            Validators.pattern('.*\\S.*'),
            Validators.maxLength(TEXT_MAX_LEN),
          ],
        ),
        organization: new FormControl(
          {
            value: this.userData.organization?.name || '',
            disabled: true,
          },
          [
            Validators.minLength(TEXT_MIN_LEN),
            Validators.maxLength(TEXT_MAX_LEN),
            Validators.required,
          ],
        ),
        email: new FormControl(
          {
            value: this.userData.email,
            disabled: this.userFormType !== USER_FORM_TYPY_INVITE,
          },
          [Validators.required, Validators.email],
        ),
        roles: new FormControl(
          {
            value: this.userData.roles,
            disabled: this.userFormType === USER_FORM_TYPE_SAVE,
          },
          [Validators.required],
        ),
        region: new FormControl(
          {
            value: this.userData.region || 'EU',
            disabled: this.userFormType !== USER_FORM_TYPY_INVITE,
          },
          [Validators.required],
        ),
        language: new FormControl(
          {
            value: this.getLanguage(),
            disabled: this.userFormType === USER_FORM_TYPE_ADMIN_SAVE,
          },
          [Validators.required],
        ),
      });
    }
  }

  getLanguage() {
    if (this.userFormType === USER_FORM_TYPE_SAVE) {
      return (
        sessionStorage.getItem('language') || this.userData.language || 'en'
      );
    } else {
      return this.userData.language || 'en';
    }
  }

  async onSubmit() {
    if (this.userForm.valid) {
      const rawForm = {
        ...this.userForm.getRawValue(),
        organization: {...this.userData.organization},
      };
      rawForm.firstName = rawForm.firstName.trim();
      rawForm.lastName = rawForm.lastName.trim();
      this.userDataChange.emit(rawForm);
    } else {
      this.userForm.markAllAsTouched();
    }
  }

  async onCancel() {
    const instance = await this.modalService.open({
      content: this.cancelModalRef,
      centered: true,
      data: this.userData,
    });

    instance.onClose.on(async (shouldCancel) => {
      if (shouldCancel) {
        const userDataCopy = JSON.parse(JSON.stringify(this.userData));
        userDataCopy.organization = this.userData.organization.name;
        this.userForm.reset(userDataCopy);
        if (this.cancelRoute !== '') {
          await this.router.navigate(['/app/list-user']);
        }
      }
    });
  }

  getFormFieldError(fieldName: string): string {
    const errors = this.userForm.controls[fieldName]?.errors;
    if (fieldName === 'firstName' && errors) {
      if (
        errors?.['required'] ||
        errors?.['pattern']?.['requiredPattern'] === '^.*\\S.*$'
      ) {
        return 'um.first_name_error_required';
      } else if (errors?.['minlength']) return 'um.first_name_error_too_short';
      else if (errors?.['maxlength']) return 'um.first_name_error_too_long';
    } else if (fieldName === 'lastName' && errors) {
      if (
        errors?.['required'] ||
        errors?.['pattern']?.['requiredPattern'] === '^.*\\S.*$'
      ) {
        return 'um.last_name_error_required';
      } else if (errors?.['minlength']) return 'um.last_name_error_too_short';
      else if (errors?.['maxlength']) return 'um.last_name_error_too_long';
    } else if (fieldName === 'email' && errors) {
      if (errors?.['required']) return 'um.email_error_required';
      else if (errors?.['email']) return 'um.email_not_valid';
    } else if (fieldName === 'organization' && errors) {
      if (errors?.['required']) return 'um.organization_error_required';
    } else if (fieldName === 'roles' && errors) {
      if (errors?.['required']) return 'um.roles_not_valid';
    } else if (fieldName === 'region' && errors) {
      if (errors?.['required']) return 'um.region_error_required';
    } else if (fieldName === 'language' && errors) {
      if (errors?.['required']) return 'um.language_error_required';
    }
    return '';
  }
}
